import React, { useState } from 'react';
import { generateSourcingStrategies } from '../services/geminiService';
import { SourcingStrategy, Vacancy } from '../types';
import { Globe, Search, Copy, ExternalLink, RefreshCw, Wand2, Lightbulb, Briefcase, User, FileText } from 'lucide-react';

interface SourcingToolProps {
    user?: any;
}

const SourcingTool: React.FC<SourcingToolProps> = ({ user }) => {
    const [jobTitle, setJobTitle] = useState('');
    const [persona, setPersona] = useState('');
    const [description, setDescription] = useState('');

    const [strategies, setStrategies] = useState<SourcingStrategy[]>([]);
    const [isGenerating, setIsGenerating] = useState(false);

    const handleGenerate = async () => {
        if (!jobTitle) {
            alert("Vul in ieder geval een functietitel in.");
            return;
        }

        setIsGenerating(true);
        try {
            const result = await generateSourcingStrategies(
                { title: jobTitle, description: description },
                persona
            );

            setStrategies(result.strategies);
        } catch (e) {
            console.error(e);
            alert("Fout bij genereren sourcing strategieën.");
        } finally {
            setIsGenerating(false);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    return (
        <div className="p-4 2xl:p-8 max-w-7xl mx-auto space-y-4 2xl:space-y-8">

            <div className="space-y-1">
                <h1 className="text-xl font-black text-slate-900 tracking-tight leading-tight flex items-center gap-2">
                    <Search className="text-[#e51b23] shrink-0" size={24} /> AI Sourcing Agent
                </h1>
                <p className="text-xs text-slate-500 font-medium max-w-3xl">
                    Gebruik deze tool om snel recruiting strings te genereren voor elke willekeurige zoekopdracht, los van een specifieke vacature.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* INPUTS */}
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm">
                        <h3 className="font-black text-slate-900 uppercase tracking-widest text-xs mb-6 flex items-center gap-2">
                            <Wand2 size={16} className="text-indigo-500" /> Input
                        </h3>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Functietitel <span className="text-red-500">*</span></label>
                                <div className="relative">
                                    <Briefcase className="absolute top-3 left-3 text-slate-400" size={16} />
                                    <input
                                        className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all"
                                        placeholder="bv. Senior Java Developer"
                                        value={jobTitle}
                                        onChange={(e) => setJobTitle(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Persona / Criteria</label>
                                <div className="relative">
                                    <User className="absolute top-3 left-3 text-slate-400" size={16} />
                                    <textarea
                                        className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all min-h-[100px]"
                                        placeholder="bv. HBO werk/denkniveau, ervaring met Spring Boot, woont in Rotterdam..."
                                        value={persona}
                                        onChange={(e) => setPersona(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Extra Context (Optioneel)</label>
                                <div className="relative">
                                    <FileText className="absolute top-3 left-3 text-slate-400" size={16} />
                                    <textarea
                                        className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all min-h-[80px]"
                                        placeholder="Specifieke keywords of 'must-haves'..."
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="pt-4">
                                <button
                                    onClick={handleGenerate}
                                    disabled={isGenerating}
                                    className="w-full py-4 bg-indigo-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-indigo-700 focus:outline-none disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-indigo-200 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
                                >
                                    {isGenerating ? (
                                        <>
                                            <RefreshCw className="w-4 h-4 animate-spin" /> Genereren...
                                        </>
                                    ) : (
                                        <>
                                            <Wand2 className="w-4 h-4" /> Genereer Strategieën Detail
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* RESULTS */}
                <div className="lg:col-span-2 space-y-6">
                    {strategies.length === 0 ? (
                        <div className="h-full min-h-[400px] flex flex-col items-center justify-center bg-slate-50 rounded-[2rem] border border-slate-200 border-dashed text-slate-400 p-12 text-center">
                            <Globe size={48} className="mb-4 opacity-20" />
                            <h3 className="font-bold text-lg text-slate-500">Nog geen resultaten</h3>
                            <p className="text-sm">Vul de gegevens links in en klik op genereren.</p>
                        </div>
                    ) : (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-8 duration-500">
                            {strategies.map((strategy, idx) => (
                                <div key={idx} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all group">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="bg-slate-100 p-3 rounded-xl">
                                                {strategy.platform.toLowerCase().includes('linkedin') ? <Globe size={20} className="text-red-" /> :
                                                    strategy.platform.toLowerCase().includes('github') ? <Globe size={20} className="text-slate-800" /> :
                                                        <Search size={20} className="text-emerald-600" />}
                                            </div>
                                            <div>
                                                <h3 className="font-black text-slate-900 text-lg">{strategy.platform}</h3>
                                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">X-Ray Search</span>
                                            </div>
                                        </div>

                                        <a
                                            href={strategy.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-indigo-100 transition-colors"
                                        >
                                            <ExternalLink size={14} /> Open in Google
                                        </a>
                                    </div>

                                    {/* TARGET PERSONA BLOCKS */}
                                    {strategy.targetPersona && (
                                        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {/* BIO CARD */}
                                            <div className="md:col-span-2 bg-slate-50 p-4 rounded-xl border border-slate-100">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                                                    <span className="text-xs font-bold text-slate-500 uppercase">Target Persona</span>
                                                </div>
                                                <p className="text-sm text-slate-700 font-medium leading-relaxed italic">"{strategy.targetPersona.bio}"</p>
                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    {strategy.targetPersona.currentJobTitles?.map((title, i) => (
                                                        <span key={i} className="px-2 py-1 bg-white border border-slate-200 rounded text-[10px] font-bold text-slate-600">{title}</span>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* VISUAL STATS */}
                                            <div className="bg-indigo-50/50 p-4 rounded-xl border border-indigo-100 flex flex-col justify-center space-y-3">
                                                <div>
                                                    <span className="text-[10px] font-bold text-indigo-400 uppercase block mb-1">Senioriteit</span>
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 h-2 bg-indigo-200 rounded-full overflow-hidden">
                                                            <div className="h-full bg-indigo-600 rounded-full" style={{ width: `${strategy.targetPersona.seniorityLevel || 50}%` }}></div>
                                                        </div>
                                                        <span className="text-xs font-black text-indigo-900">{strategy.targetPersona.seniorityLabel}</span>
                                                    </div>
                                                </div>
                                                <div>
                                                    <span className="text-[10px] font-bold text-indigo-400 uppercase block mb-1">Opleidingsniveau</span>
                                                    <span className="text-sm font-black text-indigo-900">{strategy.targetPersona.educationLevel}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* SKILLS & KEYWORDS */}
                                    {strategy.targetPersona && (
                                        <div className="mb-4 flex flex-wrap gap-2">
                                            {strategy.targetPersona.skills?.map((skill, i) => (
                                                <span key={i} className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-bold border border-emerald-100 flex items-center gap-1">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                                                    {skill}
                                                </span>
                                            ))}
                                            {strategy.targetPersona.keywords?.map((kw, i) => (
                                                <span key={i} className="px-3 py-1 bg-slate-100 text-slate-500 rounded-full text-xs font-medium border border-slate-200">
                                                    #{kw}
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 font-mono text-xs text-slate-600 break-all leading-relaxed relative group-hover:bg-indigo-50/30 group-hover:border-indigo-100 transition-colors">
                                        {strategy.query}
                                        <button
                                            onClick={() => copyToClipboard(strategy.query)}
                                            className="absolute top-2 right-2 p-2 bg-white text-slate-400 hover:text-indigo-600 rounded-lg border border-slate-200 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                                            title="Kopieer query"
                                        >
                                            <Copy size={14} />
                                        </button>
                                    </div>

                                    <div className="mt-4 flex items-start gap-2">
                                        <Lightbulb size={16} className="text-amber-500 shrink-0 mt-0.5" />
                                        <p className="text-sm text-slate-500 font-medium italic">{strategy.explanation}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default SourcingTool;
